import { RepeatType } from "./repeatType";
import dayjs from "dayjs";

const DATE_FORMAT = "DD/MM/YYYY";

export default function generateRepetitionPreview(translation, values) {

    const error = checkForRepeatErrors(values);
    if (error) {
        return `${translation("insert")} ${translation(error.label)}, ${translation(error.error)}`;
    }

    return buildPreview(values);
}

function buildPreview(values) {
    switch (values.repeatType) {
        case RepeatType.NONE:
            return buildNonePreview(values);

        case RepeatType.MONTH:
            return buildMonthPreview(values);

        case RepeatType.CUSTOM:
            return buildCustomPreview(values);

        default:
            return "";
    }
}

function buildNonePreview(values) {
    return `1. ${formatDate(values.startDate)} - ${formatDate(values.endDate)}`;
}

function buildMonthPreview(values) {
    const lines = [];

    let year = values.startMonth.getFullYear();
    let month = values.startMonth.getMonth();

    for (let i = 0; i <= values.repeatNum; i++) {
        const start = dayjs(new Date(year, month, 1)).format(DATE_FORMAT);
        month++;
        const end = dayjs(new Date(year, month, 0)).format(DATE_FORMAT);
        pushLine(lines, i, start, end);
    }

    return formatLines(lines);
}

function buildCustomPreview(values) {
    const lines = [];

    let startDate = dayjs(values.startDate);
    let endDate = dayjs(values.endDate);

    const diffDays = endDate.diff(startDate, "day");

    for (let i = 0; i <= values.repeatNum; i++) {
        pushLine(lines, i, startDate.format(DATE_FORMAT), endDate.format(DATE_FORMAT));

        // shift finestra
        startDate = endDate.add(1, "day");
        endDate = endDate.add(diffDays + 1, "day");
    }

    return formatLines(lines);
}

function pushLine(lines, index, start, end) {
    lines.push(`${index + 1}. ${start} - ${end}`);
}

function formatLines(lines) {
    return lines.length ? `${lines.join("\n")}\n` : "";
}

function formatDate(value) {
    return dayjs(Date.parse(value)).format(DATE_FORMAT);
}

/***************************/

/**
 * Aggiorna i valori quando si cambia repeatType, resettando i campi dipendenti.
 * @param {Object} values - stato attuale del form
 * @param {String} nextRepeatType - nuovo repeatType selezionato
 * @returns {Object} nuovo stato valori
 */
export function applyRepeatTypeSelection(values, nextRepeatType) {
    const newValues = { ...values };
    const prevRepeatType = values.repeatType 
        ? String(values.repeatType) 
        : RepeatType.NONE;

    const removeFields = (...fields) => {
        fields.forEach((field) => delete newValues[field]);
    };

    const isSwitchBetween = (a, b) =>
        (prevRepeatType === a && nextRepeatType === b) ||
        (prevRepeatType === b && nextRepeatType === a);

    if (isSwitchBetween(RepeatType.NONE, RepeatType.MONTH)) {
        removeFields("startDate", "repeatNum");
    }

    if (isSwitchBetween(RepeatType.NONE, RepeatType.CUSTOM)) {
        removeFields("repeatNum");
    }

    if (isSwitchBetween(RepeatType.CUSTOM, RepeatType.MONTH)) {
        removeFields("startDate");
    }

    // Reset sempre quando cambia repeatType
    removeFields("endDate", "startMonth");

    // Default per MONTH
    if (nextRepeatType === RepeatType.MONTH) {
        newValues.startMonth = new Date();
    }

    newValues.repeatType = nextRepeatType;

    return newValues;
}


/***************************/

export function checkForRepeatErrors(values) {
    const valuesCopy = { 
        ...values,
        startDate: Date.parse(values.startDate),
        endDate: Date.parse(values.endDate)
     };

    if (valuesCopy.repeatType === RepeatType.NONE) {
        return validateNone(valuesCopy);
    }

    if (valuesCopy.repeatType === RepeatType.MONTH) {
        return validateMonth(valuesCopy);
    }

    if (valuesCopy.repeatType === RepeatType.CUSTOM) {
        return validateCustom(valuesCopy);
    }

    return null;
}

/**
 * 
 * @param {String} error chiave di traduzione dell'errore 
 * @param {String} field nome campo relativo all'errore
 * @param {String} label intestazione del campo relativo all'errore
 * @returns 
 */
function createErrorObject(error, field, label) {
    return { error, field, label };
}

function validateNone(convertedValues) {
    if (!convertedValues.startDate) {
        return createErrorObject("mandatoryField", "startDate", "startDate");
    }
    if (!convertedValues.endDate) {
        return createErrorObject("mandatoryField", "endDate", "endDate");
    }
    if (convertedValues.startDate > convertedValues.endDate) {
        return createErrorObject("endDateLessThanStartDate", "endDate", "endDate");
    }
    return null;
}

function validateMonth(convertedValues) {
    if (!convertedValues.startMonth) {
        return createErrorObject("mandatoryField", "startMonth", "startMonth");
    }
    if (!convertedValues.repeatNum) {
        return createErrorObject("mandatoryField", "repeatNum", "repeatNum");
    }
    if (convertedValues.repeatNum < 1) {
        return createErrorObject("invalidValue", "repeatNum", "repeatNum");
    }
    return null;
}

function validateCustom(convertedValues) {
    if (!convertedValues.startDate) {
        return createErrorObject("mandatoryField", "startDate", "startDate");
    }
    if (!convertedValues.endDate) {
        return createErrorObject("mandatoryField", "endDate", "endDate");
    }
    if (convertedValues.startDate > convertedValues.endDate) {
        return createErrorObject("endDateLessThanStartDate", "endDate", "endDate");
    }
    if (!convertedValues.repeatNum) {
        return createErrorObject("mandatoryField", "repeatNum", "repeatNum");
    }
    if (convertedValues.repeatNum < 1) {
        return createErrorObject("invalidValue", "repeatNum", "repeatNum");
    }
    return null;
}
